import {useCallback} from 'react'
import {useAuthStore} from '@/features/auth/store/store'
import {EnvironmentSnapshot} from '../types'

type NavigatorWithUserAgentData = Navigator & {
    userAgentData?: {
        brands?: Array<{brand: string; version: string}>
        platform?: string
    }
}

const UNKNOWN = 'Unknown'

const detectBrowser = (): string => {
    const nav = navigator as NavigatorWithUserAgentData
    const brands = nav.userAgentData?.brands
    if (brands?.length) {
        const branded = brands.find(b => !/Not.A.Brand|Chromium/i.test(b.brand)) ?? brands[0]
        if (branded) return `${branded.brand} ${branded.version}`
    }
    const ua = navigator.userAgent
    const match =
        /(Edg|Edge)\/([\d.]+)/.exec(ua) ||
        /(Firefox)\/([\d.]+)/.exec(ua) ||
        /(Chrome)\/([\d.]+)/.exec(ua) ||
        /Version\/([\d.]+).*?(Safari)/.exec(ua)
    if (match) {
        const name = match[1] === 'Edg' ? 'Edge' : match[2] === 'Safari' ? 'Safari' : match[1]
        const version = match[2] === 'Safari' ? match[1] : match[2]
        return `${name} ${version}`
    }
    return UNKNOWN
}

const detectOs = (): string => {
    const nav = navigator as NavigatorWithUserAgentData
    if (nav.userAgentData?.platform) return nav.userAgentData.platform
    const ua = navigator.userAgent
    if (/Windows NT 10/.test(ua)) return 'Windows 10/11'
    if (/Windows NT/.test(ua)) return 'Windows'
    if (/Mac OS X ([\d_]+)/.test(ua)) {
        const version = /Mac OS X ([\d_]+)/.exec(ua)?.[1]?.replace(/_/g, '.')
        return version ? `macOS ${version}` : 'macOS'
    }
    if (/Android ([\d.]+)/.test(ua)) return `Android ${/Android ([\d.]+)/.exec(ua)?.[1]}`
    if (/iPhone OS ([\d_]+)/.test(ua)) return `iOS ${/iPhone OS ([\d_]+)/.exec(ua)?.[1]?.replace(/_/g, '.')}`
    if (/Linux/.test(ua)) return 'Linux'
    return UNKNOWN
}

export const useEnvironmentSnapshot = () => {
    const user = useAuthStore(state => state.user)

    return useCallback((): EnvironmentSnapshot => {
        return {
            reporterName: user?.fullName || UNKNOWN,
            reporterEmail: user?.email || UNKNOWN,
            url: window.location.href || UNKNOWN,
            browser: detectBrowser(),
            os: detectOs(),
            screenResolution: window.screen ? `${window.screen.width}×${window.screen.height}` : UNKNOWN,
            viewportSize: `${window.innerWidth}×${window.innerHeight}`,
            appVersion: import.meta.env.VITE_APP_VERSION || 'N/A',
            timestamp: new Date().toISOString()
        }
    }, [user])
}
